import { createReducer } from 'redux-starter-kit';
import {
  cancelEditUser, editUser, fetchUpdateSelf,
  fetchAddUser, fetchUsers, fetchUpdateUser,
} from './userUiActions';
import { selectRequest } from '../../../domain/requests/RequestReducer';
import {selectLocationsMap} from "../../../domain/locations/locationReducer";

/**
 * A selector for finding the current user edit state.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the inspection reason being edited.
 * @example const editState = selectEditState(state);
 */
export const selectEditState = state => state.via.ui.users.editState;

/**
 * A selector that returns the state of the request to retrieve all users action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const fetchUsersRequest = selectUsersRequest(state);
 */
export const selectUsersRequest = (state) => {
  const requestId = state.via.ui.users.requestUsersId;
  if (!requestId) return null;
  return selectRequest(state, requestId);
};

/**
 * A selector for the current 'request to create a new user ' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const createUserRequest = selectCreateUserRequest(state);
 */
export const selectCreateUserRequest = (state) => {
  const requestId = state.via.ui.users.editState.requestCreateUserId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the current request to update the current user.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const updateSelfRequest = selectUpdateSelfRequest(state);
 */
export const selectUpdateSelfRequest = (state) => {
  const requestId = state.via.ui.users.requestUpdateSelfId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectLocations = (state) => {
  const locationsMap = selectLocationsMap(state);
  const locations = Object.values(locationsMap);

  return locations || [];
};

const initialEditingState = {
  isEditing: false,
  username: null,
  requestCreateUserId: null,
  validationMessages: ({
    title: '',
    messages: [],
    visible: false,
  }),
};

const usersInitialState = {
  editState: initialEditingState,
  pendingPublishInspectionReasons: [],
  requestUsersId: null,
  requestLocationsId: null, // The request id for locations
};

/**
 * A reducer for handling the UI state changes for the Manage Users Configuration screen.
 */
const userUiReducer = createReducer(usersInitialState, {
  // A user is being edited
  [editUser]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      isEditing: true,
      userId: action.payload,
    },
  }),

  // A user is no longer being edited
  [cancelEditUser]: state => ({
    ...state,
    editState: {
      ...initialEditingState,
    },
  }),

  // A new requst has been made to add a user
  [fetchAddUser]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestCreateUserId: action.payload,
    },
  }),

  // A new request has been made to update a user.
  [fetchUpdateUser]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestCreateUserId: action.payload,
    },
  }),

  [fetchUpdateSelf]: (state, action) => ({
    ...state,
    requestUpdateSelfId: action.payload,
  }),

  // A new request has been made to load all the users from the platform
  [fetchUsers]: (state, action) => ({
    ...state,
    requestUsersId: action.payload,
  }),
});

export default userUiReducer;
